import { useState, createElement } from 'react';
import { pdf } from '@react-pdf/renderer';
import EventPDFDocument from '@/components/events/EventPDFDocument';
import { toast } from '@/hooks/use-toast';

interface Event {
  id: string;
  name: string;
  slug: string;
  event_date: string;
  event_time: string | null;
  doors_time: string | null;
  location: string;
  description: string | null;
  cause: string;
  is_featured: boolean;
  is_tentative: boolean;
}

interface Challenge {
  id: string;
  one_liner: string | null;
  problem_description: string | null;
  who_it_affects: string | null;
  why_it_matters: string | null;
  opportunity: string | null;
  solution_directions: string[] | null;
  success_metrics: string | null;
}

export const useEventPDF = () => {
  const [isGenerating, setIsGenerating] = useState(false);

  const downloadPDF = async (event: Event, challenge: Challenge | null) => {
    setIsGenerating(true);
    
    try {
      toast({
        title: "Generating PDF...",
        description: "Please wait while we create your event handout.",
      });

      // Create the document element
      const doc = createElement(EventPDFDocument, { event, challenge });
      
      // Generate PDF blob - cast to any to avoid complex type issues with react-pdf
      const blob = await pdf(doc as any).toBlob();
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Create a clean filename from event name
      const cleanName = event.name
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s+/g, '-')
        .toLowerCase();
      link.download = `${cleanName}-event-details.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: "PDF Downloaded!",
        description: "Your event handout is ready for distribution.",
      });
    } catch (error) {
      console.error('PDF generation error:', error);
      toast({
        title: "PDF Generation Failed",
        description: "There was an error creating the PDF. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return { downloadPDF, isGenerating };
};
